<template>
	<div class="growth-10k app ui-widget-container">
		<div class="hidden-xs hidden-sm">
			<!-- First tab-->
			<div
				class="tab growth-10k-tab"
				:class="{ active: isGrowthActive }"
				@click="changeActiveTab('growth')"
			>
				<h3 class="header">
					Growth of 10K
					<slot name="superscript2" />
				</h3>
			</div>
			<!-- Second tab-->
			<div
				class="tab high-low-tab"
				:class="{ active: isHighLowActive }"
				@click="changeActiveTab('highlow')"
			>
				<h3 class="header">High & Low Prices</h3>
			</div>
			<!-- First tab view-->
			<div v-if="isGrowthActive">
				<ReadMoreLink />
				<div class="row">
					<div class="chart-description col-xs-12 col-md-7">
						<span class="bold-font">For Class {{ shareClass }} Shares</span>
						,
						{{ growth10kReinvest }}
					</div>
					<a
						href="#"
						class="modal-link larger view-link pull-right col-xs-12 col-md-5"
						@click="openGrowthChart"
					>
						<i class="icon icon-zoom-in" />
						VIEW LARGER CHART
					</a>
				</div>
				<horizontal-chart :widget-data="growthChart">
					<template #Iball_primaryIndex>
						<slot name="Iball_primaryIndex1a" />
					</template>
					<template #superscript1>
						<slot name="superscript1a" />
					</template>
				</horizontal-chart>
			</div>
			<!-- Second tab view-->
			<div v-if="isHighLowActive">
				<div class="chart-description">
					<strong>For Class {{ shareClass }} Shares</strong>
					,
					{{ highLowReinvest }}
				</div>
				<horizontal-chart :widget-data="highLowChart">
					<template #Iball_primaryIndex>
						<slot name="Iball_primaryIndex1b" />
					</template>
					<template #superscript1>
						<slot name="superscript1b" />
					</template>
				</horizontal-chart>
			</div>
		</div>
		<!--First accordion item-->
		<div
			class="row visible-xs visible-sm first-item"
			:class="{ active: isGrowthActive }"
			@click="changeActiveTab('growth')"
		>
			<div class="col-md-12">
				<div class="expand-collapse" data-viewname="growth-of-10k">
					<div :class="[isGrowthActive ? 'white-arrow-down' : 'white-arrow-right']" />
					<h3>
						Growth of 10K
						<slot name="superscript3" />
					</h3>
				</div>
			</div>
		</div>
		<!--First accordion view-->
		<template v-if="isGrowthActive">
			<div class="visible-xs visible-sm">
				<ReadMoreLink />
				<div class="row">
					<div class="chart-description col-xs-12 col-md-7">
						<span class="bold-font">For Class {{ shareClass }} Shares</span>
						,
						{{ growth10kReinvest }}
					</div>
				</div>
				<horizontal-chart :widget-data="growthChart">
					<template #Iball_primaryIndex>
						<slot name="Iball_primaryIndex1c" />
					</template>
					<template #superscript1>
						<slot name="superscript1c" />
					</template>
				</horizontal-chart>
				<a
					href="#"
					class="modal-link larger view-link pull-right col-xs-12 col-md-5"
					@click="openGrowthChart"
				>
					<i class="icon icon-zoom-in" />
					VIEW LARGER CHART
				</a>
			</div>
		</template>
		<!--Second accordion item-->
		<div
			class="row visible-xs visible-sm"
			:class="{ active: isHighLowActive }"
			@click="changeActiveTab('highlow')"
		>
			<div class="col-md-12">
				<div class="expand-collapse" data-viewname="high-low-prices">
					<div :class="[isHighLowActive ? 'white-arrow-down' : 'white-arrow-right']" />
					<h3>High & Low Prices</h3>
				</div>
			</div>
		</div>
		<!--Second accordion view-->
		<div v-if="isHighLowActive" class="visible-xs visible-sm">
			<div class="chart-description">
				<strong>For Class {{ shareClass }} Shares</strong>
				,
				{{ highLowReinvest }}
			</div>
			<horizontal-chart :widget-data="highLowChart">
				<template #Iball_primaryIndex>
					<slot name="Iball_primaryIndex1d" />
				</template>
				<template #superscript1>
					<slot name="superscript1d" />
				</template>
			</horizontal-chart>
		</div>
	</div>
</template>

<script>
import HorizontalChart from './HorizontalChart';
import ReadMoreLink from '_components_/Shared/ReadMoreLink';
import * as utils from '../../../shared/utils/utils';

export default {
	name: 'Growth10k',
	components: {
		HorizontalChart,
		ReadMoreLink
	},
	props: {
		//Widget data displayed as a array of objects with label and data keys
		widgetData: Object
	},
	data() {
		return {
			isGrowthActive: true,
			isHighLowActive: false
		};
	},
	computed: {
		fundSymbol() {
			return this.widgetData?.fundSymbol ?? '';
		},
		growthChart() {
			const chartData = {
				legend: [
					this.mapLegend(this.fundSymbol, 'square', 'blue'),
					{
						...this.mapLegend(this.indexName, 'square', 'gray'),
						slot: 'g10k-legend-iball',
						IBallSlot: 'Iball_primaryIndex',
						superscriptSlot: 'superscript1'
					}
				]
			};
			if (this.widgetData?.growthChart) {
				chartData.image = this.widgetData.growthChart;
			}
			return chartData;
		},
		growthDate() {
			return this.$format.shortDate(
				this?.widgetData?.growthDate,
				'-/-/-'
			);
		},
		growth10kReinvest() {
			return `this chart tracks a hypothetical investment with dividends reinvested,
          over the last 20 years, or, since inception date if the fund has been in existence under 20 years,
          through ${this.growthDate}.`;
		},
		highLowChart() {
			const chartData = {
				legend: [this.mapLegend(this.fundSymbol, 'square', 'blue')]
			};
			if (this.widgetData?.highLowChart) {
				chartData.image = this.widgetData.highLowChart;
			}
			return chartData;
		},
		highLowDate() {
			return this.$format.shortDate(
				this.widgetData?.highLowDate,
				'-/-/-'
			);
		},
		highLowReinvest() {
			return `this chart tracks the high and low prices at NAV for ${this.fundSymbol}
      over the last 20 years, or since inception date if the fund has been in existence under 20 years,
      through ${this.highLowDate}.`;
		},
		indexName() {
			return this.widgetData?.indexName ?? '';
		},
		shareClass() {
			return this.widgetData?.shareClass ?? '';
		}
	},
	methods: {
		changeActiveTab(activeTab) {
			if (activeTab === 'growth') {
				this.isGrowthActive = true;
				this.isHighLowActive = false;
			}
			if (activeTab === 'highlow') {
				this.isHighLowActive = true;
				this.isGrowthActive = false;
			}
		},
		mapLegend(text = '--', shape, color) {
			return { text, shape, color };
		},
		openGrowthChart() {
			const data = { modal: 'charts modal', activeTab: 'growth' };
			this.$emit('openModal', data);
		}
	}
};
</script>

<style lang="less" scoped>
@import '~_css_/tabs.css';
.growth-10k {
	color: #333;
	font-family: 'AvenirNextLTW01-Regular', Arial;
	font-size: 14px;
	img {
		width: 100%;
	}
	.disclosure-link {
		font-size: 14px;
		margin: 3px 0 20px 0;
	}
	.view-link {
		color: #1d5799;
		font-family: Avenir Next LT W01 Demi, Arial, sans-serif;
		font-size: 14px;
		margin-top: 0px;
		text-decoration: none;
	}
}
.bold-font {
	font-family: 'Avenir Next LT W01 Demi', Arial;
}

.larger {
	margin-top: 10px;
}
.chart-description {
	font-family: 'AvenirNextLTW01-Regular', Arial;
	font-size: 14px;
	line-height: 20px;
	margin-bottom: 10px;
}
@media (max-width: 768px) {
	.growth-10k {
		.disclosure-link {
			margin: 20px 0 0 0;
		}
		.view-link {
			float: none !important;
			margin-left: -13px;
		}
		.chart-description {
			margin-top: 20px;
		}
	}
}
</style>
